import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import Button from "@mui/material/Button"
import InputAdornment from "@mui/material/InputAdornment"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import { useEffect } from "react"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import CloseableSnackbar from "../../../../common/components/CloseableSnackbar"
import Column, {
  PaddedContentColumn,
} from "../../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../../common/components/ConditionalLinearProgress"
import PageTopSection from "../../../../common/components/PageTopSection"
import { RowButColumnOnMobile } from "../../../../common/components/Row"
import NoResultsTableRow from "../../../../common/components/tables/NoResultsTableRow"
import ScrollableTableContainer from "../../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../../common/components/tables/TablePaginationRow"
import {
  HeaderTableCell,
  NoWrapTableCell,
  NumberTableCell,
  SortableHeaderCell,
  StickyHeaderTableCell,
  StickyTableCell,
} from "../../../../common/components/tables/cells"
import {
  formatTableDate,
  getTableSearchParams,
  useTableSearchTextState,
  useTableSort,
} from "../../../../common/components/tables/utils"
import { trimLongString } from "../../../../common/utils"
import { selectProfileLoaded } from "../../../users/reducer"
import ConciergeInboxTemplatesPanel from "../../components/ConciergeInboxTemplatesPanel"
import UpdateCreateEmailCampaignDialog from "../../components/UpdateCreateEmailCampaignDialog"
import TemplatesTabs from "../../templates/TemplatesTabs"
import { useUpdateCreateCampaignDialogState } from "../../utils"
import { canEditResource } from "../../utils/resourceAccess"

export default function EmailCampaignList() {
  const {
    updateCreateCamaignDialogOpen,
    campaignBeingEdited,
    openUpdateCreateCampaignDialog,
    handleUpdateCreateDialogClosed,
    campaigns,
    setCampaigns,
    isLoading,
    setIsLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    setTotalResultsSize,
    openSnackbar,
    openSnackbarError,
    isSnackbarOpen,
    closeSnackbar,
    snackbarMessage,
    snackbarSeverity,
  } = useUpdateCreateCampaignDialogState()
  const [searchText, setSearchText, debouncedSearchText] =
    useTableSearchTextState("q", setPageIndex)
  const { sortColumn, sortDirection, serverSort, onSortColumnChanged } =
    useTableSort("created", "desc")
  const [searchParams, setSearchParams] = useSearchParams()
  const axios = useAuthorizedAxiosClient()
  const profileLoaded = useSelector(selectProfileLoaded)

  function handleCreateCampaignButtonClicked() {
    openUpdateCreateCampaignDialog()
  }

  useEffect(
    () => {
      if (!profileLoaded) {
        return
      }

      setIsLoading(true)
      axios
        .get(`/workflows/api/email/`, {
          params: getTableSearchParams(
            debouncedSearchText,
            pageIndex,
            pageSize,
            serverSort
          ),
        })
        .then(({ data }) => {
          setCampaigns(data.results)
          setTotalResultsSize(data.count)
        })
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileLoaded, debouncedSearchText, pageIndex, serverSort, pageSize]
  )

  useEffect(
    () => {
      if (searchParams.get("gmail-auth-success")) {
        setSearchParams({})
        openSnackbar(
          "Gmail connected successfully. Now you can create new email templates."
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams]
  )

  return (
    <Column>
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <PageTopSection title="Templates" />
      <PaddedContentColumn>
        <TemplatesTabs />
        <ConciergeInboxTemplatesPanel
          medium="email"
          onStatusMessage={openSnackbar}
        />
        <RowButColumnOnMobile
          sx={{
            justifyContent: { xs: "stretch", sm: "flex-end" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mt: 1,
          }}
        >
          <TextField
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: { sm: 280 },
              maxWidth: { sm: 360 },
              flex: { xs: "1 1 auto", sm: "0 1 360px" },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateCampaignButtonClicked}
            startIcon={<AddCircleOutlineIcon />}
            sx={{
              flexShrink: 0,
              alignSelf: { xs: "stretch", sm: "auto" },
            }}
          >
            Create new template
          </Button>
        </RowButColumnOnMobile>
        <ScrollableTableContainer>
          <Table sx={{ mt: 3 }}>
            <TableHead>
              <TableRow>
                <SortableHeaderCell
                  HeaderCellComponent={StickyHeaderTableCell}
                  columnName="name"
                  label="Name"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  HeaderCellComponent={HeaderTableCell}
                  columnName="subject"
                  label="Subject"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  HeaderCellComponent={HeaderTableCell}
                  columnName="send_count"
                  label="Send times (#)"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  HeaderCellComponent={HeaderTableCell}
                  columnName="created"
                  label="Created at"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {(isLoading || !profileLoaded) && campaigns.length === 0 ? (
                Array.from({ length: 10 }).map((_, index) => (
                  <TableRow key={index}>
                    <StickyTableCell>
                      <Skeleton variant="text" width={200} />
                    </StickyTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="text" width={300} />
                    </NoWrapTableCell>
                    <NumberTableCell>
                      <Skeleton variant="text" width={80} />
                    </NumberTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="text" width={150} />
                    </NoWrapTableCell>
                  </TableRow>
                ))
              ) : campaigns.length === 0 ? (
                <NoResultsTableRow colSpan={4} />
              ) : (
                campaigns.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    sx={{
                      cursor: canEditResource(campaign) ? "pointer" : "default",
                      opacity: canEditResource(campaign) ? 1 : 0.72,
                    }}
                    onClick={() => {
                      if (!canEditResource(campaign)) return
                      openUpdateCreateCampaignDialog(campaign)
                    }}
                  >
                    <StickyTableCell title={campaign.name}>
                      {trimLongString(campaign.name)}
                    </StickyTableCell>
                    <NoWrapTableCell title={campaign.subject}>
                      {trimLongString(campaign.subject)}
                    </NoWrapTableCell>
                    <NumberTableCell>{campaign.send_count}</NumberTableCell>
                    <NoWrapTableCell>
                      {formatTableDate(campaign.created)}
                    </NoWrapTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
        <TablePaginationRow
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          pageSize={pageSize}
          setPageSize={setPageSize}
          tableRows={campaigns}
          totalResultsSize={totalResultsSize}
        />
      </PaddedContentColumn>
      <UpdateCreateEmailCampaignDialog
        open={updateCreateCamaignDialogOpen}
        onClose={handleUpdateCreateDialogClosed}
        campaign={campaignBeingEdited}
        onError={openSnackbarError}
      />

      <CloseableSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Column>
  )
}
